import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Circle, Lock, PlayCircle, ChevronRight, Clock,
  FileText, Sparkles, Download, NotebookPen, MessageSquare, ListChecks, BookOpen,
  Award, X, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { Dialog, DialogContent } from "../components/ui/dialog";
import QuizForm from "../components/QuizForm";
import LessonChat from "../components/LessonChat";
import LessonPreroll from "../components/LessonPreroll";
import { SponsorBanner } from "../components/PublicBanners";
import MarkdownText from "../components/MarkdownText";
import CourseTools from "../components/CourseTools";
import LessonCTAs from "../components/LessonCTAs";
import VideoEmbed from "../components/VideoEmbed";

// Phase 7: short, per-session trace id rendered as a small watermark at the
// bottom of the player. Lets us trace screen-grabs back to a session via
// ip_logs without exposing PII in plain text.
function sessionHash() {
  let h = sessionStorage.getItem("fc_session_trace");
  if (h) return h;
  const rnd = Math.random().toString(36).slice(2, 8).toUpperCase();
  const t = Date.now().toString(36).slice(-4).toUpperCase();
  h = `${rnd}-${t}`;
  sessionStorage.setItem("fc_session_trace", h);
  return h;
}

export default function CoursePlayer() {
  const { courseSlug, lessonId } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [course, setCourse] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const notesTimerRef = useRef(null);
  const [moduleCompleteOpen, setModuleCompleteOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [prerollDone, setPrerollDone] = useState(false);  // Phase 7

  const trace = useMemo(sessionHash, []);

  // Phase 7: global content protection inside the player route only.
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const k = e.key.toLowerCase();
      if (mod && (k === "s" || k === "p")) {
        e.preventDefault();
        toast.message("Content is protected");
        return;
      }
      if (e.key === "PrintScreen" || k === "printscreen") {
        try { navigator.clipboard.writeText("").catch(() => {}); } catch { /* noop */ }
        toast.message("Content is protected");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset preroll on lesson change so each lesson can get its own (or reuse the
  // session-skipped one if already seen).
  useEffect(() => { setPrerollDone(false); }, [lessonId]);

  // Load course on slug change
  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/learn/${courseSlug}`)
      .then((r) => {
        setCourse(r.data);
        // If no lessonId, auto-select the first lesson
        if (!lessonId) {
          const first = r.data.modules_with_progress?.[0]?.lessons?.[0];
          if (first) {
            navigate(`/learn/${courseSlug}/${first.id}`, { replace: true });
          }
        }
      })
      .catch((e) => {
        console.error("Failed to load course", e);
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail?.code === "COMING_SOON") {
          navigate(`/courses/${courseSlug}/coming-soon`, { replace: true });
        } else if (e.response?.status === 403) {
          if (detail?.action === "upgrade") {
            setUpgradeOpen(true);
          } else {
            navigate("/pricing?banner=plan_required", { replace: true });
          }
        } else {
          setError("Failed to load course");
        }
      })
      .finally(() => setLoading(false));
  }, [courseSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load lesson when lessonId changes
  useEffect(() => {
    if (!lessonId) return;
    setLessonData(null);
    api.get(`/learn/${courseSlug}/${lessonId}`)
      .then((r) => {
        setLessonData(r.data);
        setNotes(r.data.notes || "");
      })
      .catch((e) => {
        console.error("Lesson load failed", e);
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail?.code === "COMING_SOON") {
          navigate(`/courses/${courseSlug}/coming-soon`, { replace: true });
        } else if (e.response?.status === 403) {
          if (detail?.action === "upgrade") setUpgradeOpen(true);
          else navigate("/pricing?banner=plan_required", { replace: true });
        }
      });
  }, [courseSlug, lessonId, navigate]);

  // Disable right-click on video
  const onVideoContextMenu = (e) => e.preventDefault();

  const markComplete = async () => {
    if (!lessonData) return;
    setMarking(true);
    try {
      const r = await api.post(`/learn/${courseSlug}/${lessonId}/complete`);
      if (r.data.xp_awarded > 0) {
        toast.success(`+${r.data.xp_awarded} XP earned`, { description: r.data.completed ? "Lesson complete." : null });
      } else {
        toast.success("Already completed");
      }
      // Refresh course progress
      const cr = await api.get(`/learn/${courseSlug}`);
      setCourse(cr.data);
      // Refresh user XP
      try {
        const ur = await api.get("/auth/me");
        setUser(ur.data);
      } catch {}
      if (r.data.course_completed) {
        // Big confetti before redirect
        try { (await import("../lib/celebration")).fireBigConfetti(); } catch {}
        setTimeout(() => navigate(`/learn/${courseSlug}/complete`), 800);
        return;
      }
      if (r.data.module_completed) {
        try { (await import("../lib/celebration")).fireConfetti(); } catch {}
        setModuleCompleteOpen(true);
      }
    } catch (e) {
      console.error("Mark complete failed", e);
      toast.error("Could not mark complete");
    } finally {
      setMarking(false);
    }
  };

  const goToLesson = (nextId) => {
    if (nextId) navigate(`/learn/${courseSlug}/${nextId}`);
  };

  // Debounced autosave for notes (on blur OR after 800ms idle)
  const saveNotes = async (val) => {
    if (!lessonId) return;
    setSavingNotes(true);
    try {
      await api.post(`/learn/${courseSlug}/${lessonId}/notes`, { notes: val });
    } catch (e) {
      console.error("Save notes failed", e);
    } finally {
      setSavingNotes(false);
    }
  };
  const onNotesChange = (e) => {
    const v = e.target.value;
    setNotes(v);
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => saveNotes(v), 800);
  };
  const onNotesBlur = () => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    saveNotes(notes);
  };

  const currentLessonCompleted = useMemo(() => {
    if (!course || !lessonId) return false;
    for (const m of course.modules_with_progress) {
      const l = m.lessons.find((x) => x.id === lessonId);
      if (l) return l.completed;
    }
    return false;
  }, [course, lessonId]);

  if (loading) {
    return <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center">Loading…</div>;
  }
  if (upgradeOpen) {
    // Render the upgrade modal even if course load failed with 403
    return (
      <div className="min-h-screen bg-fc-navy text-white flex flex-col items-center justify-center gap-4 px-6" data-testid="course-player">
        <Dialog open={upgradeOpen} onOpenChange={(v) => { setUpgradeOpen(v); if (!v) navigate("/dashboard"); }}>
          <DialogContent className="bg-white border-fc-red text-gray-900 max-w-md" data-testid="upgrade-modal">
            <div className="py-4">
              <Lock className="text-fc-red mb-3" size={28} />
              <p className="fc-overline mb-2">EXECUTIVE TRACK</p>
              <h2 className="fc-h-section text-2xl mb-3">Upgrade to Executive</h2>
              <p className="text-gray-600 mb-6 text-sm">
                This course is part of the Executive Mindset track. Upgrade to unlock CEO-level programming.
              </p>
              <div className="flex gap-2">
                <button onClick={() => navigate("/dashboard")} className="btn-fc-outline flex-1 justify-center text-xs py-3">
                  Not now
                </button>
                <button onClick={() => navigate("/pricing")} className="btn-fc-red flex-1 justify-center text-xs py-3" data-testid="upgrade-modal-cta">
                  See Executive Plan
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  if (error || !course) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex flex-col items-center justify-center gap-4">
        <p className="text-gray-600">{error || "Course unavailable"}</p>
        <Link to="/dashboard" className="btn-fc-red">Back to dashboard</Link>
      </div>
    );
  }

  const watermark = user?.email || "freddy-compute";

  return (
    <div className="min-h-screen bg-fc-navy flex flex-col lg:flex-row" data-testid="course-player" data-surface="light">
      {/* Sidebar */}
      <aside className="lg:w-80 lg:flex-shrink-0 bg-white border-r border-gray-200 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto no-scrollbar">
        <div className="p-5 border-b border-gray-200">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 text-[10px] font-bold tracking-[0.18em] mb-3">
            <ArrowLeft size={12} /> DASHBOARD
          </Link>
          <h2 className="font-black text-lg leading-tight tracking-tight mb-3">{course.course.title}</h2>
          <div className="h-1.5 w-full bg-white/8 overflow-hidden rounded-full">
            <div
              className="h-full bg-fc-red transition-all duration-700"
              style={{ width: `${course.overall_progress_percent}%` }}
              data-testid="course-player-progress-bar"
            />
          </div>
          <p className="text-[10px] tracking-[0.18em] text-gray-500 uppercase mt-2">
            {course.lessons_completed}/{course.total_lessons} · {course.overall_progress_percent}% complete
          </p>
        </div>

        <nav className="p-3 space-y-2">
          {course.modules_with_progress.map((m, mi) => (
            <div key={m.id} className="bg-white border border-gray-100">
              <div className="px-3 py-2.5 border-b border-gray-100">
                <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-fc-red">Module {mi + 1}</p>
                <p className="text-sm font-bold mt-0.5 leading-tight">{m.title.replace(/^Module \d+: /, "")}</p>
              </div>
              <ul className="divide-y divide-white/5">
                {m.lessons.map((l) => {
                  const isCurrent = l.id === lessonId;
                  return (
                    <li key={l.id}>
                      <button
                        onClick={() => goToLesson(l.id)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-100 transition-colors ${
                          isCurrent ? "bg-fc-red/10 border-l-2 border-fc-red" : ""
                        }`}
                        data-testid={`sidebar-lesson-${l.id}`}
                      >
                        {l.completed ? (
                          <CheckCircle2 size={14} className="text-[#10B981] flex-shrink-0" />
                        ) : isCurrent ? (
                          <span className="w-2 h-2 rounded-full bg-fc-red flex-shrink-0" />
                        ) : (
                          <Circle size={14} className="text-white/25 flex-shrink-0" />
                        )}
                        <span className={`text-xs truncate flex-1 ${isCurrent ? "text-fc-red font-bold" : "text-gray-600"}`}>
                          {l.title}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{l.duration_minutes}m</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        {lessonData ? (
          <>
            {/* Video block */}
            <div
              className="relative bg-black select-none"
              data-testid="video-container"
              onContextMenu={(e) => { e.preventDefault(); toast.message("Content is protected"); }}
              onDragStart={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
            >
              {!prerollDone ? (
                <LessonPreroll lessonId={lessonId} onFinish={() => setPrerollDone(true)} />
              ) : (
                <div className="relative aspect-video max-w-5xl mx-auto">
                  <VideoEmbed
                    url={lessonData.lesson.video_url}
                    className="w-full h-full bg-black"
                    testid="lesson-video"
                    videoProps={{
                      onContextMenu: onVideoContextMenu,
                      disablePictureInPicture: true,
                    }}
                  />
                  {/* Invisible watermark overlay — diagonal tiled email pattern.
                      mix-blend-mode: difference keeps it visible on light AND
                      dark frames; opacity stays low for screen viewing. */}
                  <div
                    className="pointer-events-none absolute inset-0 select-none overflow-hidden"
                    style={{ opacity: 0.015, mixBlendMode: "difference" }}
                    aria-hidden="true"
                    data-testid="video-watermark"
                  >
                    {Array.from({ length: 24 }).map((_, i) => (
                      <div
                        key={`${lessonId}-${i}`}
                        className="text-white text-xs whitespace-nowrap"
                        style={{
                          position: "absolute",
                          top: `${(i * 11) % 100}%`,
                          left: `${(i * 17) % 100}%`,
                          transform: "rotate(-22deg)",
                          letterSpacing: "0.18em",
                        }}
                      >
                        {watermark}
                      </div>
                    ))}
                  </div>
                  {/* Phase 7: session trace hash — tiny, gray, bottom-right.
                      Lets us trace screen-grabs back to a session via ip_logs. */}
                  <div
                    className="pointer-events-none absolute bottom-2 right-3 text-[9px] tracking-[0.18em] text-gray-400 font-mono select-none"
                    aria-hidden="true"
                    data-testid="session-trace"
                  >
                    session: {trace}
                  </div>
                </div>
              )}
            </div>

            {/* Phase 7: sponsor banner slot above the action bar (lesson_player location) */}
            <SponsorBanner location="lesson_player" />

            {/* Phase 8: inline CTA blocks below the video */}
            {lessonData?.lesson && (
              <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-5">
                <LessonCTAs
                  courseSlug={courseSlug}
                  lessonId={lessonData.lesson.id}
                  courseId={lessonData.course_id || course?.id}
                  position="below_video"
                />
              </div>
            )}

            {/* Action bar */}
            <div className="border-b border-gray-200 bg-white/70">
              <div className="max-w-5xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="fc-overline text-[10px] mb-2">{lessonData.course_title}</p>
                  <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="lesson-title">{lessonData.lesson.title}</h1>
                  <p className="text-gray-500 text-xs mt-1 flex items-center gap-2"><Clock size={12} /> {lessonData.lesson.duration_minutes} min</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={markComplete}
                    disabled={marking || currentLessonCompleted}
                    className={currentLessonCompleted ? "btn-fc-outline cursor-default" : "btn-fc-red"}
                    data-testid="mark-complete-btn"
                  >
                    {currentLessonCompleted ? (
                      <><CheckCircle2 size={16} /> Completed</>
                    ) : marking ? (
                      "Saving…"
                    ) : (
                      <><CheckCircle2 size={16} /> Mark Complete</>
                    )}
                  </button>
                  {lessonData.next_lesson_id && (
                    <button onClick={() => goToLesson(lessonData.next_lesson_id)} className="btn-fc-outline" data-testid="next-lesson-btn">
                      Next <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
              <Tabs defaultValue="transcript" data-testid="lesson-tabs">
                <TabsList className="bg-white border border-gray-200 rounded-none p-0 h-auto flex flex-wrap gap-0">
                  <TabsTrigger value="transcript" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-transcript">
                    <FileText size={14} className="mr-2" /> Transcript
                  </TabsTrigger>
                  <TabsTrigger value="ai-summary" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-ai-summary">
                    <Sparkles size={14} className="mr-2" /> AI Summary
                  </TabsTrigger>
                  <TabsTrigger value="resources" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-resources">
                    <Download size={14} className="mr-2" /> Resources
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-notes">
                    <NotebookPen size={14} className="mr-2" /> Notes
                  </TabsTrigger>
                  <TabsTrigger value="chat" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-chat">
                    <MessageSquare size={14} className="mr-2" /> AI Chat
                  </TabsTrigger>
                  <TabsTrigger value="tools" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-tools">
                    <Wrench size={14} className="mr-2" /> Tools
                  </TabsTrigger>
                  {lessonData.quiz && (
                    <TabsTrigger value="quiz" className="rounded-none text-xs tracking-[0.18em] font-bold uppercase data-[state=active]:bg-fc-red data-[state=active]:text-white px-4 py-3" data-testid="tab-quiz">
                      <ListChecks size={14} className="mr-2" /> Quiz
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="transcript" className="mt-6">
                  <div className="bg-white border border-gray-200 p-6 sm:p-8 whitespace-pre-wrap text-gray-700 leading-relaxed text-sm" data-testid="transcript-content">
                    {lessonData.lesson.transcript}
                  </div>
                </TabsContent>

                <TabsContent value="ai-summary" className="mt-6">
                  <div className="bg-white border border-gray-200 p-6 sm:p-8 space-y-6" data-testid="ai-summary-content">
                    <div>
                      <p className="fc-overline mb-3">Summary</p>
                      <ul className="space-y-2">
                        {(lessonData.lesson.ai_summary || []).map((s, i) => (
                          <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                            <Sparkles size={14} className="text-fc-red flex-shrink-0 mt-0.5" />
                            <span className="flex-1"><MarkdownText variant="light">{s}</MarkdownText></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="fc-overline mb-3">Key Takeaways</p>
                      <ul className="space-y-2">
                        {(lessonData.lesson.ai_key_takeaways || []).map((s, i) => (
                          <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                            <BookOpen size={14} className="text-fc-red flex-shrink-0 mt-0.5" />
                            <span className="flex-1"><MarkdownText variant="light">{s}</MarkdownText></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="resources" className="mt-6">
                  <div className="bg-white border border-gray-200 p-6 sm:p-8" data-testid="resources-content">
                    {(lessonData.lesson.downloadable_resources || []).length === 0 ? (
                      <p className="text-gray-500 text-sm">No downloadable resources for this lesson yet. (Instructor portal in Phase 5 will let teachers upload PDFs, worksheets, and prompt packs.)</p>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {lessonData.lesson.downloadable_resources.map((r, i) => (
                          <li key={i} className="py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText size={16} className="text-fc-red" />
                              <span className="text-gray-800 text-sm">{r.title || r.name}</span>
                            </div>
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-fc-red text-xs tracking-[0.18em] font-bold uppercase">
                              Download →
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-6">
                  <div className="bg-white border border-gray-200 p-6 sm:p-8" data-testid="notes-content">
                    <div className="flex items-center justify-between mb-3">
                      <p className="fc-overline">Your notes</p>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-gray-500">{savingNotes ? "Saving…" : "Auto-saved"}</span>
                    </div>
                    <textarea
                      value={notes}
                      onChange={onNotesChange}
                      onBlur={onNotesBlur}
                      placeholder="Capture your takeaways here. They auto-save."
                      className="w-full min-h-[280px] bg-white border border-gray-200 focus:border-fc-red text-gray-900 p-4 outline-none text-sm leading-relaxed"
                      data-testid="notes-textarea"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="chat" className="mt-6">
                  <LessonChat lessonId={lessonData.lesson.id} />
                </TabsContent>

                <TabsContent value="tools" className="mt-6">
                  <CourseTools
                    courseSlug={courseSlug}
                    courseId={lessonData.course_id || course?.id}
                  />
                </TabsContent>

                {lessonData.quiz && (
                  <TabsContent value="quiz" className="mt-6">
                    <div className="bg-white border border-gray-200 p-6 sm:p-8" data-testid="quiz-content">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <p className="fc-overline">Module Quiz</p>
                          <h3 className="text-xl font-black mt-1">{lessonData.quiz.title}</h3>
                          <p className="text-gray-500 text-xs mt-1">Pass with {lessonData.quiz.passing_score}% or higher.</p>
                        </div>
                      </div>
                      <QuizForm
                        quiz={lessonData.quiz}
                        courseSlug={courseSlug}
                        lessonId={lessonId}
                        onPassed={() => toast.success("Quiz passed!")}
                      />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </>
        ) : (
          <div className="min-h-screen flex items-center justify-center text-gray-500">Loading lesson…</div>
        )}
      </main>

      {/* Module complete modal */}
      <Dialog open={moduleCompleteOpen} onOpenChange={setModuleCompleteOpen}>
        <DialogContent className="bg-white border-fc-red text-gray-900 max-w-md" data-testid="module-complete-modal">
          <div className="text-center py-6">
            <Award className="mx-auto text-fc-red mb-4" size={48} />
            <h2 className="fc-h-section text-3xl mb-2">Module Complete!</h2>
            <p className="text-gray-600 mb-4">+100 XP added to your account.</p>
            <button onClick={() => setModuleCompleteOpen(false)} className="btn-fc-red w-full justify-center">Keep going</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade modal (for staff opening Executive lessons) */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="bg-white border-fc-red text-gray-900 max-w-md" data-testid="upgrade-modal">
          <div className="py-4">
            <Lock className="text-fc-red mb-3" size={28} />
            <p className="fc-overline mb-2">EXECUTIVE TRACK</p>
            <h2 className="fc-h-section text-2xl mb-3">Upgrade to Executive</h2>
            <p className="text-gray-600 mb-6 text-sm">
              This course is part of the Executive Mindset track. Upgrade to unlock CEO-level programming.
            </p>
            <div className="flex gap-2">
              <button onClick={() => { setUpgradeOpen(false); navigate("/dashboard"); }} className="btn-fc-outline flex-1 justify-center text-xs py-3">
                Not now
              </button>
              <button onClick={() => navigate("/pricing")} className="btn-fc-red flex-1 justify-center text-xs py-3" data-testid="upgrade-modal-cta">
                See Executive Plan
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
